import React, { forwardRef, useMemo, useState } from 'react';
import { v4 } from 'uuid';
import { handleElementKeyEvent } from '../../../util/events.util';
import { Feedback } from '../Feedback/feedback';
import { Label } from '../Label';

const invalidNumberCharacters = new Set(['e', 'E', '+', '-']);

type HTMLInputTypeAttribute =
  | 'button'
  | 'checkbox'
  | 'color'
  | 'date'
  | 'datetime-local'
  | 'email'
  | 'file'
  | 'hidden'
  | 'image'
  | 'month'
  | 'number'
  | 'password'
  | 'radio'
  | 'range'
  | 'reset'
  | 'search'
  | 'submit'
  | 'tel'
  | 'text'
  | 'time'
  | 'url'
  | 'week';

interface TextInputProps {
  label?: string;
  placeholder?: string;

  value?: string | number;
  onChange?: (value: string) => void;

  disabled?: boolean;
  mandatory?: boolean;
  feedback?: ((value: string) => Feedback.Props | undefined) | null;

  leading?: React.ReactNode;
  trailing?: React.ReactNode;
  tooltip?: React.ReactNode;

  type?: HTMLInputTypeAttribute;

  onFocus?: (event: React.FocusEvent<HTMLInputElement, Element>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement, Element>) => void;

  className?: string;
  id?: string;

  min?: number;
  max?: number;
  step?: number;

  autoComplete?: string;
  name?: string;
  readOnly?: boolean;
}

const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  {
    label,
    placeholder,
    value,
    onChange,
    disabled = false,
    mandatory = false,
    feedback,
    leading,
    trailing,
    tooltip,
    type = 'text',
    onFocus,
    onBlur,
    className = '',
    id,
    min,
    max,
    step,
    autoComplete = 'off',
    name,
    readOnly = false,
  }: TextInputProps,
  ref,
) {
  const [inputId] = useState<string>(id || v4());
  const [feedbackProps, setFeedbackProps] = useState<Feedback.Props | undefined>(undefined);

  const borderColorClass = useMemo(() => {
    if (feedbackProps) return Feedback.borders[feedbackProps.type];

    return 'border-gray-default';
  }, [feedbackProps]);

  const backgroundColorClass = useMemo(() => {
    if (disabled) return 'bg-gray-disabled';

    return 'bg-white';
  }, [disabled]);

  const textColorClass = useMemo(() => {
    if (disabled) return 'text-gray-400';

    return 'text-gray-900';
  }, [disabled]);

  const cursorClass = useMemo(() => {
    if (disabled) return 'cursor-not-allowed';

    return '';
  }, [disabled]);

  const handleChange = (value: string) => {
    if (!onChange) return;
    onChange(value);
    if (feedback == null) return;
    setFeedbackProps(
      (() => {
        const mandatoryFeedback = mandatory && Feedback.mandatory(value);
        return mandatoryFeedback || feedback(value);
      })(),
    );
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (type === 'number' && invalidNumberCharacters.has(event.key)) {
      event.preventDefault();
      return;
    }

    handleElementKeyEvent(event, 'Escape', event => event.currentTarget.blur());
  };

  const input = (
    <div className={`grid gap-1 ${cursorClass}`}>
      <div
        className={`focusable ${borderColorClass} ${backgroundColorClass} ${textColorClass} ${className} flex h-10 items-center gap-2 overflow-hidden rounded-2 border stroke-gray-500 py-2 pl-3 pr-2 text-sm`}
      >
        {leading && leading}
        {onChange ? (
          <input
            onKeyDown={handleKeyDown}
            ref={ref}
            className={`unset w-full flex-grow overflow-ellipsis outline-none placeholder:text-gray-500 ${cursorClass} ${
              type === 'search'
                ? '[&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none [&::-webkit-search-results-button]:appearance-none [&::-webkit-search-results-decoration]:appearance-none'
                : ''
            }`}
            type={type}
            name={name || `field-${inputId}`}
            id={inputId}
            value={value ?? ''} // Not || because value could be 0 and this is only here to handle undefined
            disabled={disabled}
            readOnly={readOnly}
            placeholder={placeholder}
            onChange={event => handleChange(event.currentTarget.value)}
            onFocus={onFocus}
            onBlur={onBlur}
            autoComplete={autoComplete}
            autoCorrect='off'
            title={value?.toString()}
            min={min}
            max={max}
            step={step}
          />
        ) : (
          <span title={value?.toString()} className='flex-grow overflow-hidden text-ellipsis text-nowrap'>
            {value}
          </span>
        )}
        {trailing && trailing}
      </div>
      {feedbackProps?.text && (
        <span className='flex gap-1'>
          {Feedback.icons[feedbackProps.type]}
          <p className={`text-xs ${Feedback.textColors[feedbackProps.type]}`}>{feedbackProps.text}</p>
        </span>
      )}
    </div>
  );

  if (!label) return input;

  return (
    <Label inputId={id} label={label} mandatory={mandatory} tooltip={tooltip}>
      {input}
    </Label>
  );
});

export default TextInput;
